
import React from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

interface LoadedScoreProps {
  score?: number;
  maxScore?: number;
  stage?: string;
  className?: string;
}

const LoadedScore = ({ score = 0, maxScore = 120, stage = 'Explorer', className }: LoadedScoreProps) => {
  const progress = Math.round((score / maxScore) * 100);
  
  return (
    <div className={`container-content py-4 md:py-8 ${className}`}>
      <Card className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
          <div className="relative w-24 h-24 md:w-32 md:h-32 flex-shrink-0 mx-auto md:mx-0">
            <div className="absolute inset-0 rounded-full bg-primary/10"></div>
            <div className="absolute inset-0 rounded-full bg-primary/20"></div>
            <div className="absolute inset-2 rounded-full bg-card flex items-center justify-center flex-col">
              <span className="text-2xl md:text-4xl font-bold text-primary">{score}</span>
              <span className="text-xs text-muted-foreground mt-0.5 md:mt-1">/ {maxScore}</span>
              <span className="text-[8px] md:text-[10px] text-muted-foreground mt-0.5">Loaded Score™</span>
            </div>
          </div>
          
          <div className="flex-1 space-y-3 md:space-y-4 text-center md:text-left">
            <div>
              <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-2">
                <h3 className="text-lg md:text-xl font-medium text-primary flex items-center justify-center md:justify-start gap-2">
                  {stage}
                  <Badge variant="secondary" className="ml-1 md:ml-2 bg-primary/10 text-primary text-xs">
                    Your Level
                  </Badge>
                </h3>
                <span className="text-xs md:text-sm text-muted-foreground mt-1 md:mt-0">
                  {score}/{maxScore}
                </span>
              </div>
              <Progress value={progress} className="h-1.5 md:h-2" />
            </div>
            
            <p className="text-xs md:text-sm text-muted-foreground px-2 md:px-0">
              {getStageDescription(stage)}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

const getStageDescription = (stage: string): string => {
  switch (stage) {
    case 'Explorer':
      return "You're building foundational skills. Focus on core methodologies and expanding your experience.";
    case 'Achiever':
      return "You're developing solid expertise. Continue expanding your strategic impact and leadership capabilities.";
    case 'Specialist':
      return "You're demonstrating advanced expertise. Look for opportunities to lead larger initiatives and mentor others.";
    case 'Authority':
      return "You're shaping direction at a high level. Focus on thought leadership and organizational impact.";
    case 'Guru':
      return "You're defining the future of your field. Continue innovating and sharing your expertise with the community.";
    default:
      return "Continue developing your skills and expertise to advance your career.";
  }
};

export default LoadedScore;
